import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { withFlags, type FlagsStateContainer } from '@confluence/flags';
import { useSessionData } from '@confluence/session-data';
import { expVal } from '@confluence/feature-experiments';
import { useFireEventOnce } from '../use-fire-event-once';

// TODO: Remove export of this hook once the CrossJoinNudgesWithFlags is fully rolled out
export const useCrossJoinNudges = ({
	flags,
	execNudge = false,
}: {
	flags: FlagsStateContainer;
	execNudge: boolean;
}) => {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticEventOnce = useFireEventOnce();
	const { push } = useRouteActions();
	useEffect(() => {
		const execNudges = async () => {
			const {
				checkJpdCrossJoinNudgeEligibility,
				showJpdCrossJoinNudge,
				triggerJiraCrossJoinNudge,
			} = await import(
				/* webpackChunkName: "loadable-cross-join-nudges-services" */ '../../services/run-cross-join-nudges'
			);

			const isJpdCrossJoinNudgesEligible = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
			});

			isJpdCrossJoinNudgesEligible
				? await showJpdCrossJoinNudge({ cloudId, userId, flags, createAnalyticsEvent, push })
				: await triggerJiraCrossJoinNudge({ cloudId, userId, flags, createAnalyticsEvent, push });
		};
		const execNudgesWithLoom = async () => {
			const { checkLoomCrossJoinNudgeEligibility } = await import(
				/* webpackChunkName: "loadable-loom-cross-join-nudges-services" */ '../../services/loom-cross-join-nudge'
			);

			if (await checkLoomCrossJoinNudgeEligibility()) {
				// TODO: trigger loom nudge
			} else {
				await execNudges();
			}
		};

		const orchestrateNudges = async () => {
			const { checkMsrCrossJoinNudgeEligibility, setEnrolledInMsr } = await import(
				/* webpackChunkName: "loadable-msr-cross-join-nudges-services" */ '../../services/msr-cross-join-nudge'
			);

			if (await checkMsrCrossJoinNudgeEligibility()) {
				setEnrolledInMsr();
				// NOTE: AA Test, to be removed
				expVal('aa_for_twcg-437-cjn-msr-m1', 'cohort', 'not-enrolled');

				fireAnalyticEventOnce(
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'exposed',
							actionSubject: 'feature',
							attributes: {
								flagKey: 'aa_for_twcg-437-cjn-msr-m1',
								value: expVal('aa_for_twcg-437-cjn-msr-m1', 'cohort', 'not-enrolled'),
							},
						},
					}),
				);

				// TODO: Remove once AA test is complete
				await execNudges();

				// TODO: show MSR nudge
			} else {
				await execNudgesWithLoom();
			}
		};

		const init = async () => {
			if (fg('puma-326-cjn-orchestrator-confluence')) {
				await orchestrateNudges();
			} else {
				// exec old implementation
				await execNudges();
			}
		};

		if (execNudge) {
			void init();
		}
	}, [cloudId, createAnalyticsEvent, flags, push, userId, execNudge, fireAnalyticEventOnce]);
};

export const CrossJoinNudgesWithFlags = withFlags<{ execNudge: boolean }>(
	({ flags, execNudge = false }) => {
		useCrossJoinNudges({ flags, execNudge });
		return null;
	},
);
